// surveyLocalStorage.js – optimizado: helpers compartidos, constantes extraídas

const DRAFT_PREFIX       = 'survey_draft_';
const SAVED_PREFIX       = 'survey_saved_';
const CURRENT_DRAFT_KEY  = 'current_survey_draft_id';

// Tiempos de expiración precalculados (ms)
const DRAFT_EXPIRY_MS      = 7  * 24 * 60 * 60 * 1000; // 7 días
const SAVED_FLAG_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

// ─── Helper interno ───────────────────────────────────────────────────────
// Envuelve cualquier acceso a localStorage para capturar QuotaExceededError
// y SecurityError (modo privado / cookies bloqueadas) en un solo lugar,
// evitando los ~10 bloques try/catch idénticos que había antes.
function safeStorage(fn, fallback = null) {
  try {
    return fn();
  } catch (error) {
    console.error('[surveyLocalStorage]', error);
    return fallback;
  }
}

// ─── API pública ──────────────────────────────────────────────────────────

/**
 * Guarda el borrador de la encuesta en localStorage.
 * No guarda si la encuesta ya fue enviada a Firestore.
 */
export const saveSurveyDraft = (urlId, surveyData) =>
  safeStorage(() => {
    // Evita un getItem extra: isSurveySaved también usa safeStorage,
    // así que llamamos directo y cortocircuitamos con !==null.
    if (localStorage.getItem(`${SAVED_PREFIX}${urlId}`) !== null) return false;

    localStorage.setItem(
      `${DRAFT_PREFIX}${urlId}`,
      JSON.stringify({ data: surveyData, timestamp: Date.now(), urlId }),
    );
    return true;
  }, false);

/**
 * Carga el borrador de localStorage.
 * Retorna null si: no existe, expiró o ya fue guardado en Firestore.
 */
export const loadSurveyDraft = (urlId) =>
  safeStorage(() => {
    // Si ya fue guardado en Firestore limpiamos el draft y salimos
    if (localStorage.getItem(`${SAVED_PREFIX}${urlId}`) !== null) {
      localStorage.removeItem(`${DRAFT_PREFIX}${urlId}`);
      return null;
    }

    const raw = localStorage.getItem(`${DRAFT_PREFIX}${urlId}`);
    if (!raw) return null;

    let draft;
    try {
      draft = JSON.parse(raw);
    } catch {
      // JSON corrupto — eliminar y salir
      localStorage.removeItem(`${DRAFT_PREFIX}${urlId}`);
      return null;
    }

    if (Date.now() - draft.timestamp > DRAFT_EXPIRY_MS) {
      localStorage.removeItem(`${DRAFT_PREFIX}${urlId}`);
      return null;
    }

    return draft.data;
  });

/**
 * Elimina el borrador de localStorage.
 */
export const deleteSurveyDraft = (urlId) =>
  safeStorage(() => {
    localStorage.removeItem(`${DRAFT_PREFIX}${urlId}`);
    return true;
  }, false);

/**
 * Marca la encuesta como guardada en Firestore y elimina el borrador.
 */
export const markSurveySaved = (urlId) =>
  safeStorage(() => {
    localStorage.setItem(`${SAVED_PREFIX}${urlId}`, Date.now().toString());
    localStorage.removeItem(`${DRAFT_PREFIX}${urlId}`);
    return true;
  }, false);

/**
 * Verifica si la encuesta ya fue guardada en Firestore.
 */
export const isSurveySaved = (urlId) =>
  safeStorage(() => localStorage.getItem(`${SAVED_PREFIX}${urlId}`) !== null, false);

/**
 * Elimina el flag de guardado para un urlId.
 */
export const clearSavedFlag = (urlId) =>
  safeStorage(() => {
    localStorage.removeItem(`${SAVED_PREFIX}${urlId}`);
    return true;
  }, false);

/**
 * Limpia borradores expirados y flags de guardado antiguos.
 * Se recorre localStorage una sola vez para minimizar el acceso al storage.
 */
export const clearExpiredDrafts = () =>
  safeStorage(() => {
    const now  = Date.now();
    // Snapshot de las claves para no mutar mientras iteramos
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.startsWith(DRAFT_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const { timestamp } = JSON.parse(raw);
          if (now - timestamp > DRAFT_EXPIRY_MS) localStorage.removeItem(key);
        } catch {
          localStorage.removeItem(key); // JSON corrupto
        }
      } else if (key.startsWith(SAVED_PREFIX)) {
        const ts = parseInt(localStorage.getItem(key), 10);
        if (isNaN(ts) || now - ts > SAVED_FLAG_EXPIRY_MS) localStorage.removeItem(key);
      }
    }
  });

/**
 * Obtiene el urlId del borrador activo.
 */
export const getCurrentDraftId = () =>
  safeStorage(() => localStorage.getItem(CURRENT_DRAFT_KEY));

/**
 * Persiste el urlId del borrador activo.
 */
export const setCurrentDraftId = (urlId) =>
  safeStorage(() => {
    localStorage.setItem(CURRENT_DRAFT_KEY, urlId);
    return true;
  }, false);

/**
 * Elimina el urlId del borrador activo.
 */
export const clearCurrentDraftId = () =>
  safeStorage(() => {
    localStorage.removeItem(CURRENT_DRAFT_KEY);
    return true;
  }, false);